import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ClearProducts, FetchProductsBySearch } from 'src/app/features/produits/store/produits.actions';
import { AppState } from 'src/app/core/app.state';
import { IProduct } from 'src/app/features/produits/models/products.models';
import { Observable, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IParseProduct } from '../../models/quotations.models';
import { IPagination } from 'src/app/shared/models/page.model';
import { QuotesService } from '../../services/quotations.services';
import { ProductCodeService } from 'src/app/core/services/product-code.service';
import { FTUE_ANCHORS } from 'src/app/core/ftue/ftue-anchors';
import { FtuePresenterService } from 'src/app/core/ftue/ftue-presenter.service';
import { FTUE_TIP_IDS } from 'src/app/core/ftue/ftue-tip.registry';

interface ProductMatch {
  ai_order_line_name: string;
  match_with_product_id: number;
}

interface ProductSelection {
  id: number | null;
  name: string;
  product?: IProduct | null;
}

type MatchingDialogMode = 'unmatched_ai' | 'single_order_line' | 'side_by_side';

@Component({
  selector: 'app-matching-missing-products-selector',
  templateUrl: './matching-missing-products-selector.component.html',
  styleUrls: ['./matching-missing-products-selector.component.scss']
})
export class MatchingMissingProductsSelectorComponent implements OnInit, AfterViewInit, OnDestroy {
  products$: Observable<IProduct[]> = this.store.select((state) => state.products.productsBySearch);
  baseUrl = environment.BASE_URL_API;
  placeholder: string = 'assets/icones/default-image-product.svg';
  products: IProduct[] = [];
  private searchResults: IProduct[] = [];
  private alternativesOptions: { [key: string]: IProduct[] } = {};
  displayProducts: IParseProduct[] = [];
  dialogMode: MatchingDialogMode = 'unmatched_ai';
  singleProductKey: string | null = null;
  get isSingleProductMode(): boolean {
    return this.dialogMode === 'single_order_line' || this.dialogMode === 'side_by_side';
  }

  // --- Legacy state for unmatched_ai mode ---
  matches: { [key: string]: ProductSelection } = {};
  currentEditingProduct: string = '';
  searchInputs: { [key: string]: string } = {};
  subscriptions: Subscription[] = [];
  toBeRemovedProducts: string[] = [];
  searchPage = 1;
  searchMetadata: IPagination | null = null;
  isLoadingMore = false;
  isSearchLoading = false;
  private panelScrollListener: (() => void) | null = null;
  private panelEl: HTMLElement | null = null;
  private activeTrigger: MatAutocompleteTrigger | null = null;
  @ViewChildren('searchInput') searchInputEls!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('singleSearchInput') singleSearchInputEls!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChild('singleTrigger') singleAutoTrigger!: MatAutocompleteTrigger;
  private singlePanelScrollListener: (() => void) | null = null;
  private singlePanelEl: HTMLElement | null = null;

  // --- New state for single_order_line redesign ---
  originalProduct: IProduct | null = null;
  selectedProductId: number | null = null;
  candidateProducts: IProduct[] = [];
  /** Id of the candidate the API flagged `is_original`. Only set when re-matching an
   *  already-matched line, so the candidate list can label it "Original Product". */
  originalAlternativeId: number | null = null;
  hasAlternatives: boolean = false;
  searchInput: string = '';

  /**
   * "Always match this way" state. When true, save() carries alwaysMatch in
   * its close payload so the caller flags the PATCH as permanent. Default
   * off; the toggle is only offered when the user picked an alternative.
   */
  alwaysMatch: boolean = false;

  /** The specific extracted code the user chose for external-reference creation.
   *  Pre-selected to the first extracted code when codes exist. */
  selectedAmtwCode: string | null = null;

  /** Whether the "Always ignore" toggle is currently ON. */
  alwaysIgnore: boolean = false;

  /** The specific extracted code the user chose to ignore (for multi-code lines). */
  selectedIgnoreCode: string | null = null;

  isLoadingAlternatives = false;

  /**
   * Customer the order is bound to (sale.order.id_company). Threaded in via
   * MAT_DIALOG_DATA so the dialog can hide actions that require a customer
   * when the order has none (alias-only paths still allowed — global alias).
   */
  orderCustomerId: number | null = null;

  /** Same payload the dialog received via MAT_DIALOG_DATA (mode, products, orderCustomerId, …). */
  @Input() data: any;

  /** Replaces dialogRef.close(payload) — emits the result the caller acts on. */
  @Output() completed = new EventEmitter<any>();
  /** Replaces dialogRef.close() with no payload — a cancel / no-op close. */
  @Output() cancelled = new EventEmitter<void>();

  /** Anchor keys the template exposes to the FTUE framework (WB-7535). */
  readonly ftueAnchors = FTUE_ANCHORS;

  constructor(
    private store: Store<AppState>,
    private translate: TranslateService,
    private dialog: MatDialog,
    private quotesService: QuotesService,
    public productCode: ProductCodeService,
    private ftuePresenter: FtuePresenterService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(ClearProducts());

    this.dialogMode = (this.data?.mode as MatchingDialogMode) || 'unmatched_ai';
    this.displayProducts = (this.data?.products as IParseProduct[]) || (this.data?.product ? [this.data.product] : []);
    const rawCustomerId = this.data?.orderCustomerId;
    this.orderCustomerId = typeof rawCustomerId === 'number' && rawCustomerId > 0 ? rawCustomerId : null;

    if (this.isSingleProductMode) {
      this.initSingleProductMode();
    } else {
      this.initUnmatchedAiMode();
    }

    const sub1 = this.products$.subscribe((products) => {
      this.searchResults = products || [];
      if (this.isSingleProductMode) {
        this.updateSearchResults();
      } else {
        this.updateDisplayedOptions();
      }
    });
    this.subscriptions.push(sub1);
  }

  private initSingleProductMode(): void {
    const product = this.displayProducts[0];
    if (!product) return;

    const key = product.name || 'single_product';
    this.singleProductKey = key;
    this.hasAlternatives = !!product.has_alternatives;

    if (!this.data.isUnmatched) {
      this.originalProduct = this.parseProductToIProduct(product);
      this.selectedProductId = this.originalProduct.id ?? null;
    } else {
      this.originalProduct = null;
      this.selectedProductId = null;
    }

    const codes = this.extractedCodesList;
    if (codes.length > 0) {
      this.selectedAmtwCode = codes[0];
      this.selectedIgnoreCode = codes[0];
    }

    if (this.data.isUnmatched) {
      this.loadUnmatchedAlternatives(product);
    } else if (this.hasAlternatives && product.order_line_id && this.data.quotationId) {
      this.isLoadingAlternatives = true;
      const isMatched = !!product.matched;
      const lineKey = String(product.order_line_id);
      const sub = this.quotesService.getOrderAlternativesBulk(this.data.quotationId).subscribe({
        next: (res) => {
          const bulkMap = res.object ?? {};
          const rawAlts: IParseProduct[] = Array.isArray(bulkMap[lineKey]) ? bulkMap[lineKey] : [];
          // The API includes the pre-correction product flagged `is_original`. When the line
          // is not matched yet it duplicates the top card, so hide it; when re-matching an
          // already-matched line, keep it and remember its id so the list can label it.
          const original = rawAlts.find((a) => a.is_original);
          this.originalAlternativeId = isMatched && original
            ? ((original.id ?? original.product_id) as number)
            : null;
          const visibleAlts = isMatched ? rawAlts : rawAlts.filter((a) => !a.is_original);
          this.candidateProducts = this.toProductOptions(visibleAlts);
          this.isLoadingAlternatives = false;
        },
        error: () => {
          this.isLoadingAlternatives = false;
        }
      });
      this.subscriptions.push(sub);
    }

    const metaSub = this.store.select((s) => s.products.searchMetadata).subscribe((meta) => {
      this.searchMetadata = meta;
      this.isLoadingMore = false;
      this.isSearchLoading = false;
    });
    this.subscriptions.push(metaSub);
  }

  /**
   * Fetch alternatives for an UNMATCHED line and populate the candidate cards, mirroring the
   * matched flow. The bulk endpoint is keyed by the unmatched line's `id` and cached per
   * quotation, so the first open loads the whole map and later opens reuse it. Unmatched lines
   * have no pre-correction original, so there is no `is_original` entry to filter or label.
   */
  private loadUnmatchedAlternatives(product: IParseProduct): void {
    if (!product.id || !this.data.quotationId) return;
    this.isLoadingAlternatives = true;
    const lineKey = String(product.id);
    const sub = this.quotesService.getUnmatchedAlternativesBulk(this.data.quotationId).subscribe({
      next: (res) => {
        const bulkMap = res.object ?? {};
        const rawAlts: IParseProduct[] = Array.isArray(bulkMap[lineKey]) ? bulkMap[lineKey] : [];
        this.candidateProducts = this.toProductOptions(rawAlts);
        this.originalAlternativeId = null;
        this.isLoadingAlternatives = false;
      },
      error: () => {
        this.isLoadingAlternatives = false;
      }
    });
    this.subscriptions.push(sub);
  }

  private initUnmatchedAiMode(): void {
    this.displayProducts.forEach((p: IParseProduct) => {
      this.matches[p.name] = { id: null, name: '', product: null };
      this.searchInputs[p.name] = '';
    });

    const sub2 = this.store.select((s) => s.products.searchMetadata).subscribe((meta) => {
      this.searchMetadata = meta;
      this.isLoadingMore = false;
      this.isSearchLoading = false;
    });
    this.subscriptions.push(sub2);
  }

  ngAfterViewInit(): void {
    if (!this.isSingleProductMode) return;
    // Auto-focus search if there are alternatives or product is unmatched
    if (this.hasAlternatives || !this.originalProduct) {
      setTimeout(() => {
        const el = this.singleSearchInputEls?.first?.nativeElement;
        el?.focus();
      }, 300);
    }
  }

  // ============================================================
  // Single product mode — new selection UX
  // ============================================================

  isOriginalSelected(): boolean {
    return this.originalProduct !== null && this.selectedProductId === this.originalProduct.id;
  }

  isCandidateSelected(product: IProduct): boolean {
    return this.selectedProductId === product.id;
  }

  isOriginalCandidate(product: IProduct): boolean {
    return this.originalAlternativeId !== null && product.id === this.originalAlternativeId;
  }

  selectOriginal(): void {
    if (this.originalProduct) {
      this.selectedProductId = this.originalProduct.id ?? null;
      this.alwaysIgnore = false;
    }
  }

  selectCandidate(product: IProduct): void {
    this.selectedProductId = product.id ?? null;
    this.alwaysIgnore = false;
    this.requestManualMatchTip();
  }

  getOriginalLabel(): string {
    const line = this.displayProducts[0];
    // Already manually matched → the top card reflects the manual match, not the stale AI method.
    if (line?.matched) return this.translate.instant('quotations_and_orders.match_method.manual_match');
    const mm = (line?.ai_match_method || '').toLowerCase();
    if (mm === 'exact_alias') return this.translate.instant('quotations_and_orders.match_method.perfect_alias');
    if (mm === 'exact_code') return this.translate.instant('quotations_and_orders.match_method.perfect_code');
    if (mm === 'hybrid_reranked') return this.translate.instant('quotations_and_orders.match_method.semantic_ranking');
    if (mm === 'preferred' || mm === 'preferred_global') return this.translate.instant('quotations_and_orders.match_method.preferred');
    return this.hasAlternatives
      ? this.translate.instant('quotations_and_orders.match_method.semantic_ranking')
      : this.translate.instant('quotations_and_orders.match_method.perfect_code');
  }

  getOriginalSublabel(): string {
    const line = this.displayProducts[0];
    if (line?.matched) return '';
    const mm = (line?.ai_match_method || '').toLowerCase();
    const value = (line?.ai_match_value || '').trim();
    if (!value) return '';
    if (mm === 'preferred' || mm === 'preferred_global') {
      if (!/^\d+$/.test(value)) return '';
      const key = mm === 'preferred_global'
        ? 'quotations_and_orders.match_method.preferred_frequency_global'
        : 'quotations_and_orders.match_method.preferred_frequency';
      return this.translate.instant(key, { count: value });
    }
    if (mm === 'exact_alias') return `${this.translate.instant('quotations_and_orders.match_method.alias_value')}: ${value}`;
    if (mm === 'exact_code') {
      const field = (line?.ai_match_field || '').toLowerCase();
      const fieldToKey: Record<string, string> = {
        sku: 'quotations_and_orders.match_method.sku_value',
        ean: 'quotations_and_orders.match_method.ean_value',
        extra_identifier: 'quotations_and_orders.match_method.extra_identifier_value',
        external_reference: 'quotations_and_orders.match_method.external_ref_value',
      };
      const labelKey = fieldToKey[field];
      if (labelKey) return `${this.translate.instant(labelKey)}: ${value}`;
      return value;
    }
    return '';
  }

  getOriginalIcon(): string {
    const line = this.displayProducts[0];
    if (line?.matched) return 'manual-match-icon';
    const mm = (line?.ai_match_method || '').toLowerCase();
    if (mm === 'hybrid_reranked') return 'check-list-icon';
    if (mm === 'preferred' || mm === 'preferred_global') return 'check-icon';
    if (mm === 'exact_code' || mm === 'exact_alias') return 'check-icon';
    return this.hasAlternatives ? 'check-list-icon' : 'check-icon';
  }

  onSingleSearch(event: any): void {
    const value = event.target.value;
    this.searchInput = value;

    if (!value || value.length < 1) {
      this.store.dispatch(ClearProducts());
      this.isSearchLoading = false;
      return;
    }

    this.searchPage = 1;
    this.isSearchLoading = true;
    const filterRequest = {
      filters: [{ key: 'status', operator: 'equals', value: 'Published' }]
    };
    this.store.dispatch(FetchProductsBySearch({
      keyword: value,
      filter: filterRequest,
      includeDeals: false,
      page: { number: 1, size: 10 },
      append: false,
      skipGlobalLoader: true
    }));
  }

  loadMoreSingleProducts(): void {
    if (this.isLoadingMore || !this.searchInput) return;
    if (!this.searchMetadata || this.searchPage >= this.searchMetadata.pages) return;

    this.isLoadingMore = true;
    this.isSearchLoading = true;
    this.searchPage++;
    const filterRequest = {
      filters: [{ key: 'status', operator: 'equals', value: 'Published' }]
    };
    this.store.dispatch(FetchProductsBySearch({
      keyword: this.searchInput,
      filter: filterRequest,
      includeDeals: false,
      page: { number: this.searchPage, size: 10 },
      append: true,
      skipGlobalLoader: true
    }));
  }

  onSingleAutocompleteOpened(): void {
    setTimeout(() => {
      const panel = this.singleAutoTrigger?.autocomplete?.panel?.nativeElement;
      if (panel) {
        // Clear any stale listener (e.g. panel reopened before a prior cleanup ran) before attaching.
        this.removeSinglePanelScrollListener();
        this.singlePanelEl = panel;
        this.singlePanelScrollListener = this.onSinglePanelScroll.bind(this);
        panel.addEventListener('scroll', this.singlePanelScrollListener);
      }
    });
  }

  onSingleAutocompleteClosed(): void {
    this.removeSinglePanelScrollListener();
  }

  private onSinglePanelScroll(): void {
    const panel = this.singleAutoTrigger?.autocomplete?.panel?.nativeElement;
    if (!panel) return;
    const atBottom = panel.scrollHeight - panel.scrollTop - panel.clientHeight < 50;
    if (atBottom && !this.isLoadingMore) {
      this.loadMoreSingleProducts();
    }
  }

  private removeSinglePanelScrollListener(): void {
    // Remove from the element we actually attached to — the trigger's panel ref may already be gone.
    if (this.singlePanelScrollListener && this.singlePanelEl) {
      this.singlePanelEl.removeEventListener('scroll', this.singlePanelScrollListener);
    }
    this.singlePanelScrollListener = null;
    this.singlePanelEl = null;
  }

  onSingleProductSelect(event: MatAutocompleteSelectedEvent): void {
    const selected = event.option.value as IProduct;
    // Add to front if not already there, or move to front if already a candidate
    const withoutSelected = this.candidateProducts.filter(p => p.id !== selected.id);
    this.candidateProducts = [selected, ...withoutSelected];
    // Auto-select
    this.selectedProductId = selected.id ?? null;
    this.alwaysIgnore = false;
    this.searchInput = '';
    this.store.dispatch(ClearProducts());
    this.requestManualMatchTip();
  }

  private updateSearchResults(): void {
    // Products already on a quote line are intentionally NOT excluded: the user may
    // want to (re)match a line to a product that already exists elsewhere on the quote.
    // We only drop the original product and anything already shown as a candidate card,
    // to avoid duplicate entries in the dropdown.
    const candidateIds = new Set(this.candidateProducts.map(p => p.id));
    const originalId = this.originalProduct?.id;

    this.products = (this.searchResults || []).filter(
      p => p.id !== originalId && !candidateIds.has(p.id!)
    );
  }

  get alternativesCount(): number {
    return this.candidateProducts.length;
  }

  /**
   * Match-method-aware AMTW mode:
   *  - 'none': exact_code + catalog field (sku/ean/extra_identifier) — no KB promotion
   *  - 'cascade_only': exact_code + external_reference — silent cascade repoint
   *  - 'cascade_plus_options': exact_alias — cascade + optional ext_ref creation
   *  - 'full': hybrid_reranked / null — ext_ref from code OR alias from name
   */
  get amtwMode(): 'none' | 'cascade_only' | 'cascade_plus_options' | 'full' {
    const line = this.displayProducts[0];
    if (!line) return 'full';
    const method = (line.ai_match_method || '').toLowerCase();
    const field = (line.ai_match_field || '').toLowerCase();

    if (method === 'exact_code') {
      if (field === 'external_reference') return 'cascade_only';
      return 'none';
    }
    if (method === 'exact_alias') return 'cascade_plus_options';
    return 'full';
  }

  /**
   * Whether the active AMTW action requires a customer on the order.
   *  - cascade_only: cascade re-points a customer-scoped KB row
   *  - full + extracted codes: ext_ref creation is customer-scoped
   *  - cascade_plus_options + resolved code: ext_ref creation is customer-scoped
   * Alias-only paths (full without codes, cascade_plus_options without code)
   * are allowed without a customer — the backend writes a global alias.
   */
  get amtwActionRequiresCustomer(): boolean {
    const mode = this.amtwMode;
    if (mode === 'cascade_only') return true;
    if (mode === 'full' && this.hasExtractedCodes) return true;
    if (mode === 'cascade_plus_options' && !!this.resolvedAmtwCode) return true;
    return false;
  }

  /** True when the action requires a customer but the order has none. */
  get amtwBlockedByMissingCustomer(): boolean {
    return this.amtwActionRequiresCustomer && !this.orderCustomerId;
  }

  /**
   * Whether the AMTW toggle bar should be shown. Requires:
   *  - single-product mode
   *  - user changed the product selection
   *  - mode has actionable options (cascade_plus_options or full)
   *  - full mode has a pattern to promote (extracted code or name/match value)
   *  - the action is not blocked by a missing customer
   */
  /**
   * The always-match bar just became available, so point the first-time tip at
   * it — before the user confirms, which is the only moment the toggle can
   * still be flipped (WB-7535).
   */
  private requestManualMatchTip(): void {
    if (this.showAlwaysMatchToggle) {
      this.ftuePresenter.request(FTUE_TIP_IDS.ORDER_LINE_MANUAL_MATCH);
    }
  }

  get showAlwaysMatchToggle(): boolean {
    if (!this.isSingleProductMode) return false;
    if (!this.selectedProductId || this.selectedProductId === this.originalProduct?.id) return false;
    if (this.amtwBlockedByMissingCustomer) return false;
    const mode = this.amtwMode;
    if (mode === 'full') return this.hasExtractedCodes || !!this.customerKey;
    return mode === 'cascade_plus_options';
  }

  /** Whether the ignore toggle bar should be shown. Inverse of AMTW: shown when
   *  line is unmatched, no product is selected, and at least one extracted code exists.
   *  Per D-01/D-02: occupies the same slot as AMTW bar, mutually exclusive.
   *  Per D-05: only in single_order_line and side_by_side modes.
   *  Per D-09: hidden if no extracted codes. */
  get showAlwaysIgnoreToggle(): boolean {
    if (!this.isSingleProductMode) return false;
    if (!this.data.isUnmatched) return false;
    if (this.selectedProductId) return false;
    return this.extractedCodesList.length > 0;
  }

  /** Resolves the ignore code to send in the close payload.
   *  Single code: returns that code. Multi-code: returns user's selection. */
  get resolvedIgnoreCode(): string | null {
    const codes = this.extractedCodesList;
    if (codes.length === 0) return null;
    if (codes.length === 1) return codes[0];
    return this.selectedIgnoreCode;
  }

  get selectedProductName(): string {
    if (!this.selectedProductId) return '';
    const picked = this.candidateProducts.find(p => p.id === this.selectedProductId);
    return picked?.name || '';
  }

  /**
   * Pattern future AI orders will match on. When the AI supplied the authoritative
   * value it matched on we surface it directly; otherwise we fall back to the
   * first extracted code (for exact_code / preferred) or the extracted name.
   *
   * WB-7534 — `preferred` / `preferred_global` are excluded from the match_value
   * shortcut: for those methods the matcher overloads `match_value` with the
   * purchase frequency rather than an identifier (WB-6443,
   * `product_matcher._finish_preferred`), so keying a rule off it would propose a
   * meaningless numeric alias. The key must come from the buyer's own line item.
   * Returns '' when neither a buyer code nor a usable name exists, which also
   * hides the AMTW affordance via `showAlwaysMatchToggle`.
   */
  get customerKey(): string {
    const line = this.displayProducts[0];
    if (!line) return '';
    const matchValue = (line.ai_match_value || '').trim();
    if (matchValue && !this.isPreferredMatch) return matchValue;
    if (this.isCodeMatch || this.isPreferredMatch) {
      const codes = (line.ai_extracted_codes || '').split(',').map(c => c.trim()).filter(Boolean);
      if (codes.length) return codes[0];
    }
    return (line.ai_extracted_name || '').trim();
  }

  get isCodeMatch(): boolean {
    const mm = (this.displayProducts[0]?.ai_match_method || '').toLowerCase();
    return mm === 'exact_code';
  }

  /** WB-6443 purchase-history matches, where `match_value` carries the frequency. */
  get isPreferredMatch(): boolean {
    const mm = (this.displayProducts[0]?.ai_match_method || '').toLowerCase();
    return mm === 'preferred' || mm === 'preferred_global';
  }

  get extractedCodesList(): string[] {
    const line = this.displayProducts[0];
    if (!line) return [];
    return (line.ai_extracted_codes || '')
      .replace(/[\[\]']/g, '')
      .split(',')
      .map(c => c.trim())
      .filter(Boolean);
  }

  get amtwHelpKey(): string {
    const mode = this.amtwMode;
    if (mode === 'cascade_only') return 'quotations_and_orders.amtw.help_cascade_extref';
    if (mode === 'cascade_plus_options') {
      const count = this.extractedCodesList.length;
      if (count === 0) return 'quotations_and_orders.amtw.help_cascade_alias';
      if (count === 1) return 'quotations_and_orders.amtw.help_cascade_alias_and_ref';
      return 'quotations_and_orders.amtw.help_cascade_alias_and_refs';
    }
    const count = this.extractedCodesList.length;
    if (count === 0) return 'quotations_and_orders.amtw.help_alias_only';
    if (count === 1) return 'quotations_and_orders.amtw.help_single_ref';
    return 'quotations_and_orders.amtw.help_pick_ref';
  }

  get amtwHelpParams(): Record<string, string | number> {
    return { count: this.extractedCodesList.length };
  }

  /**
   * Resolves the amtw_code to send to the backend. For 'full' and
   * 'cascade_plus_options' modes with codes, returns the user-selected code
   * (or the single available code). Returns null for alias-only paths.
   */
  get resolvedAmtwCode(): string | null {
    const codes = this.extractedCodesList;
    if (codes.length === 0) return null;
    if (codes.length === 1) return codes[0];
    return this.selectedAmtwCode;
  }

  /** Whether extracted codes exist and an inline code selector should render. */
  get hasExtractedCodes(): boolean {
    if (this.amtwMode === 'none' || this.amtwMode === 'cascade_only') return false;
    return this.extractedCodesList.length > 0;
  }

  /** Whether to show the code-picker dropdown (2+ extracted codes). */
  get showCodePicker(): boolean {
    if (this.amtwMode === 'none' || this.amtwMode === 'cascade_only') return false;
    return this.extractedCodesList.length > 1;
  }

  get willOverrideKbEntry(): { type: 'alias' | 'ext_ref'; value: string } | null {
    const line = this.displayProducts[0];
    if (!line) return null;
    const method = (line.ai_match_method || '').toLowerCase();
    const field = (line.ai_match_field || '').toLowerCase();
    const value = (line.ai_match_value || '').trim();
    if (!value) return null;
    if (method === 'exact_alias') return { type: 'alias', value };
    if (method === 'exact_code' && field === 'external_reference') return { type: 'ext_ref', value };
    return null;
  }

  get amtwOverrideNoticeKey(): string | null {
    const entry = this.willOverrideKbEntry;
    if (!entry) return null;
    return entry.type === 'alias'
      ? 'quotations_and_orders.amtw.override_notice_alias'
      : 'quotations_and_orders.amtw.override_notice_ext_ref';
  }

  get amtwOverrideNoticeParams(): Record<string, string> {
    const entry = this.willOverrideKbEntry;
    return entry ? { value: entry.value } : {};
  }

  // ============================================================
  // Legacy unmatched_ai mode (unchanged)
  // ============================================================

  onSearch(event: any, productName: string) {
    const value = event.target.value;
    this.searchInputs[productName] = value;
    this.updateDisplayedOptions();

    if(value && value != '' && value.length >= 1){
      this.searchPage = 1;
      this.isSearchLoading = true;
      const filterRequest = {
        filters: [{ key: 'status', operator: 'equals', value: 'Published' }]
      };
      this.store.dispatch(
        FetchProductsBySearch({
          keyword: value,
          filter: filterRequest,
          includeDeals: false,
          page: { number: 1, size: 10 },
          append: false,
          skipGlobalLoader: true
        })
      );
    }
    else if(value == '' || value == null){
      this.isSearchLoading = false;
      this.store.dispatch(ClearProducts());
    }
  }

  onInputFocus(productName: string, trigger?: MatAutocompleteTrigger) {
    this.currentEditingProduct = productName;
    this.activeTrigger = trigger || null;
    this.updateDisplayedOptions();
    trigger?.openPanel();
  }

  onProductSelect(event: MatAutocompleteSelectedEvent) {
    if (this.currentEditingProduct) {
      const selectedProduct = event.option.value as IProduct;
      this.matches[this.currentEditingProduct] = {
        id: selectedProduct.id || null,
        name: selectedProduct.name,
        product: selectedProduct
      };
      this.searchInputs[this.currentEditingProduct] = '';
      this.store.dispatch(ClearProducts());
      this.updateDisplayedOptions();
    }
  }

  removeSelectedProduct(productName: string) {
    this.matches[productName] = { id: null, name: '', product: null };
    this.searchInputs[productName] = '';
    this.store.dispatch(ClearProducts());
    this.updateDisplayedOptions();
  }

  getSelectedProductName(aiProductName: string): string {
    return this.matches[aiProductName]?.product?.name || this.matches[aiProductName]?.name || '';
  }

  getSearchInputValue(productName: string): string {
    return this.searchInputs[productName] || '';
  }

  hasSelectedProduct(productName: string): boolean {
    return !!(this.matches[productName]?.id);
  }

  removeProduct(productName: string) {
    if (this.isSingleProductMode) return;
    this.toBeRemovedProducts.push(productName);
    this.matches[productName] = { id: null, name: '', product: null };
    this.searchInputs[productName] = '';
    this.store.dispatch(ClearProducts());
    this.displayProducts = this.displayProducts.filter((product: any) => product.name !== productName);
  }

  // ============================================================
  // Save / Close
  // ============================================================

  save() {
    if (this.isSingleProductMode) {
      // Handle ignore toggle in any single-product mode
      if (this.alwaysIgnore && this.resolvedIgnoreCode) {
        this.completed.emit({
          action: 'ignore',
          code: this.resolvedIgnoreCode,
          extracted_name: this.displayProducts[0]?.ai_extracted_name || '',
        });
        return;
      }

      if (this.selectedProductId && this.selectedProductId !== this.originalProduct?.id) {
        const selected = this.candidateProducts.find(p => p.id === this.selectedProductId);
        const isCascade = this.amtwMode === 'cascade_only' || this.amtwMode === 'cascade_plus_options';
        const alwaysMatch = isCascade || (this.showAlwaysMatchToggle && this.alwaysMatch);
        const sendAmtwCode = this.amtwMode === 'cascade_plus_options'
          ? this.alwaysMatch
          : alwaysMatch;
        const amtwCode = sendAmtwCode ? this.resolvedAmtwCode : null;
        if (this.dialogMode === 'side_by_side') {
          this.completed.emit({ selectedProduct: selected || null, alwaysMatch, amtwCode });
        } else {
          this.completed.emit({
            selectedProduct: { id: this.selectedProductId, name: selected?.name || '' },
            alwaysMatch,
            amtwCode,
          });
        }
      } else {
        this.cancelled.emit();
      }
      return;
    }

    // unmatched_ai mode
    const matchesArray: ProductMatch[] = Object.keys(this.matches)
      .filter(aiProductName => this.matches[aiProductName]?.id)
      .map(aiProductName => ({
        ai_order_line_name: aiProductName,
        match_with_product_id: this.matches[aiProductName].id!
      }));

    this.completed.emit({ matches: matchesArray, toBeRemovedProducts: this.toBeRemovedProducts });
  }

  deleteProduct() {
    const isIgnore = this.alwaysIgnore && this.resolvedIgnoreCode;
    const confirmRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        title: this.translate.instant(
          isIgnore ? 'quotations_and_orders.ignore.remove_and_ignore' : 'quotations_and_orders.remove_product'
        ),
        text: this.translate.instant(
          isIgnore ? 'quotations_and_orders.ignore.remove_and_ignore_confirmation' : 'quotations_and_orders.remove_product_confirmation'
        ),
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translate.instant(
          isIgnore ? 'quotations_and_orders.ignore.remove_and_ignore' : 'common.remove'
        ),
        confirm_class: 'btn-danger',
      }
    });
    confirmRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        if (isIgnore) {
          this.completed.emit({
            action: 'ignore',
            code: this.resolvedIgnoreCode,
            extracted_name: this.displayProducts[0]?.ai_extracted_name || '',
          });
        } else {
          this.completed.emit({ action: 'delete' });
        }
      }
    });
  }

  close() {
    this.cancelled.emit();
  }

  loadMoreProducts(): void {
    if (this.isLoadingMore || !this.currentEditingProduct) return;
    const keyword = this.searchInputs[this.currentEditingProduct];
    if (!keyword) return;
    if (!this.searchMetadata || this.searchPage >= this.searchMetadata.pages) return;

    this.isLoadingMore = true;
    this.isSearchLoading = true;
    this.searchPage++;
    const filterRequest = {
      filters: [
        {
          key: 'status',
          operator: 'equals',
          value: "Published"
        }
      ]
    };
    this.store.dispatch(
      FetchProductsBySearch({
        keyword,
        filter: filterRequest,
        includeDeals: false,
        page: { number: this.searchPage, size: 10 },
        append: true,
        skipGlobalLoader: true
      })
    );
  }

  onAutocompleteOpened(): void {
    setTimeout(() => {
      const panel = this.activeTrigger?.autocomplete?.panel?.nativeElement;
      if (panel) {
        // Clear any stale listener (e.g. panel reopened before a prior cleanup ran) before attaching.
        this.removePanelScrollListener();
        this.panelEl = panel;
        this.panelScrollListener = this.onPanelScroll.bind(this);
        panel.addEventListener('scroll', this.panelScrollListener);
      }
    });
  }

  onAutocompleteClosed(): void {
    this.removePanelScrollListener();
  }

  onPanelScroll(): void {
    const panel = this.activeTrigger?.autocomplete?.panel?.nativeElement;
    if (!panel) return;
    const threshold = 50;
    const atBottom = panel.scrollHeight - panel.scrollTop - panel.clientHeight < threshold;
    if (atBottom && !this.isLoadingMore) {
      this.loadMoreProducts();
    }
  }

  private removePanelScrollListener(): void {
    // Remove from the element we actually attached to — the trigger's panel ref may already be gone.
    if (this.panelScrollListener && this.panelEl) {
      this.panelEl.removeEventListener('scroll', this.panelScrollListener);
    }
    this.panelScrollListener = null;
    this.panelEl = null;
  }

  ngOnDestroy(): void {
    this.removePanelScrollListener();
    this.removeSinglePanelScrollListener();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  // ============================================================
  // Helpers
  // ============================================================

  private parseProductToIProduct(p: IParseProduct): IProduct {
    return {
      id: (p.id || p.product_id) as any,
      product_id: p.product_id as any,
      product_template_id: p.product_template_id as any,
      name: p.name as any,
      sku: p.sku as any,
      erp_id: p.erp_id as any,
      image: p.image as any,
      description: p.description as any,
      sales_price: (p.sales_price ?? p.price_unit) as any,
      final_price: p.final_price as any,
      discount: p.discount as any,
      increment: p.increment as any,
      max_qte: p.max_qte as any,
      min_qte: p.min_qte as any,
      status: (p.status as any) || 'Published',
      tag_ids: ([] as any),
      barcode: (p.barcode as any) || 0,
      cost: (p.cost as any) || 0,
      handling_units: p.handling_units as any,
      handling_unit_id: p.handling_unit_id as any,
    } as IProduct;
  }

  private toProductOptions(alternatives: IParseProduct[]): IProduct[] {
    return alternatives.map((a) => this.parseProductToIProduct(a));
  }

  private updateDisplayedOptions() {
    // Products already on a quote line are intentionally NOT excluded (the user may match
    // to a product that exists elsewhere on the quote). We still exclude products already
    // picked for another line within this dialog to avoid double-assignment.
    const matchedProductIds = new Set(
      Object.values(this.matches)
        .filter((sel) => sel.id !== null)
        .map((sel) => sel.id)
    );

    const currentKey = this.currentEditingProduct;
    const localAlternatives = currentKey ? (this.alternativesOptions[currentKey] || []) : [];

    const combined = [...localAlternatives, ...(this.searchResults || [])];
    const uniqueById = new Map<number, IProduct>();
    combined.forEach((p) => {
      const id = p?.id as any;
      if (id !== null && id !== undefined && !uniqueById.has(id)) {
        uniqueById.set(id, p);
      }
    });

    this.products = Array.from(uniqueById.values()).filter(
      (product) => !matchedProductIds.has(product.id!)
    );
  }
}
